import { SCAN_ACTIVE_STATUSES, type ScanStatus } from '@radar/contracts';
import { queryOptions } from '@tanstack/react-query';
import type { InferRequestType } from 'hono/client';
import { api, call } from '@/lib/api';
import { qk, type ScanEstimateInput, type ScansListFilters } from '@/lib/query-keys';
import type { ScansSearch } from '@/lib/search-schemas';

// Accès à `/api/v1/scans` (`api.md` § Scans) : fonctions `call(api.scans…)` et `queryOptions`
// partagées par les hooks, les loaders de route et la sidebar.

export type CreateScanInput = InferRequestType<typeof api.scans.$post>['json'];
export type ScanEventsQueryInput = InferRequestType<
  (typeof api.scans)[':id']['events']['$get']
>['query'];
export type ScansListData = Awaited<ReturnType<typeof listScans>>;
export type ScanDetailData = Awaited<ReturnType<typeof getScan>>;
export type ScanEventsData = Awaited<ReturnType<typeof listScanEvents>>;
export type ScanResultsData = Awaited<ReturnType<typeof getScanResults>>;
export type ScanFunnelData = Awaited<ReturnType<typeof getScanFunnel>>;
export type ScanEstimateData = Awaited<ReturnType<typeof estimateScan>>;

/** Polling de la fiche (`frontend.md` § 3) et de la liste (`/scans`, 30 s). */
export const SCAN_POLL_MS = 5_000;
export const SCANS_LIST_POLL_MS = 30_000;

export function isScanActive(status: ScanStatus | null | undefined): boolean {
  return (
    status !== null &&
    status !== undefined &&
    (SCAN_ACTIVE_STATUSES as readonly ScanStatus[]).includes(status)
  );
}

/** Search params de `/scans` → query string typée de `GET /scans` (tri fixe `createdAt desc`). */
export function toScansQuery(search: ScansSearch, marketId: string | null): ScansListFilters {
  return {
    page: String(search.page),
    pageSize: String(search.pageSize),
    sort: 'createdAt',
    order: 'desc',
    ...(marketId ? { marketId } : {}),
    ...(search.status && search.status.length > 0 ? { status: search.status } : {}),
  };
}

/** Compteur de la sidebar : `total` des scans `running`, une ligne suffit. */
export const RUNNING_SCANS_QUERY: ScansListFilters = { status: 'running', pageSize: '1' };

export function listScans(query: ScansListFilters) {
  return call(api.scans.$get({ query }));
}

export const scansListOptions = (query: ScansListFilters) =>
  queryOptions({ queryKey: qk.scans.list(query), queryFn: () => listScans(query) });

export function getScan(id: string) {
  return call(api.scans[':id'].$get({ param: { id } }));
}

/** Fiche d'un scan : polling 5 s tant que `queued | running | paused_budget` (fonction, jamais un intervalle fixe). */
export const scanDetailOptions = (id: string) =>
  queryOptions({
    queryKey: qk.scans.detail(id),
    queryFn: () => getScan(id),
    refetchInterval: (query) => (isScanActive(query.state.data?.status) ? SCAN_POLL_MS : false),
  });

export function listScanEvents(id: string, query: ScanEventsQueryInput) {
  return call(api.scans[':id'].events.$get({ param: { id }, query }));
}

export function getScanResults(id: string) {
  return call(api.scans[':id'].results.$get({ param: { id } }));
}

export const scanResultsOptions = (id: string) =>
  queryOptions({ queryKey: qk.scans.results(id), queryFn: () => getScanResults(id) });

/** `GET /scans/:id/funnel` (T35) : entonnoir et motifs de chute d'un scan `completed`. */
export function getScanFunnel(id: string) {
  return call(api.scans[':id'].funnel.$get({ param: { id } }));
}

export const scanFunnelOptions = (id: string) =>
  queryOptions({ queryKey: qk.scans.funnel(id), queryFn: () => getScanFunnel(id) });

export function estimateScan(json: ScanEstimateInput) {
  return call(api.scans.estimate.$post({ json }));
}

/** Estimation (`POST /scans/estimate`) mise en cache par charge utile ; erreurs rendues par le formulaire. */
export const scanEstimateOptions = (json: ScanEstimateInput) =>
  queryOptions({
    queryKey: qk.scans.estimate(json),
    queryFn: () => estimateScan(json),
    staleTime: 60_000,
    meta: { toast: false },
  });

export const createScan = (json: CreateScanInput) => call(api.scans.$post({ json }));

export const cancelScan = (id: string) => call(api.scans[':id'].cancel.$post({ param: { id } }));

export const resumeScan = (id: string) => call(api.scans[':id'].resume.$post({ param: { id } }));

export const retryScan = (id: string) => call(api.scans[':id'].retry.$post({ param: { id } }));
